import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { debounce, FieldTree, FormField, SchemaPathTree } from '@angular/forms/signals';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { of } from 'rxjs';
import { ACB_IEC60947Dto, InDto, MceApiService } from '../../../../core/mceapi.client.generated';
import { match, none, Option, some } from '../../../../shared/functions/functional';
import { Load } from '../../load/load.model';

export interface ACBIEC60947 {
  assemblyGuid: string;
  rating: number;
  autoRating: boolean;
  ir: number;
}

export function buildACBIEC60947Schema(path: SchemaPathTree<ACBIEC60947>) {
  debounce(path.ir, 500);
}

export function createACBIEC60947(dto: Option<ACB_IEC60947Dto>) {
  return signal<ACBIEC60947>(
    match(
      () =>({ assemblyGuid: '', rating: 0, autoRating: true, ir: 1 }),
      (x: ACB_IEC60947Dto): ACBIEC60947 => {
        if (x.assemblyGuid === undefined || x.rating === undefined || x.autoRating === undefined || x.ir === undefined) {
          throw new Error('ACB DTO is missing required values.');
        }

        return {
          assemblyGuid: x.assemblyGuid,
          rating: x.rating,
          autoRating: x.autoRating,
          ir: x.ir
        };
      })(dto)
  );
}

export function createACBIEC60947Dto(model: FieldTree<ACBIEC60947>) {
  return signal<Option<ACB_IEC60947Dto>>(
    model.assemblyGuid().value() === '' || model.rating().value() === 0
      ? none
      : some(
        {
        assemblyGuid: model.assemblyGuid().value(),
        rating: model.rating().value(),
        autoRating: model.autoRating().value(),
        ir: model.ir().value() 
      }));
}


@Component({
  selector: 'protection-acb',
  imports: [FormField, MatSelectModule, MatSlideToggleModule, MatSliderModule],
  templateUrl: './acb.component.html',
})
export class AcbComponent {

  readonly form = input.required<FieldTree<ACBIEC60947>>();
  readonly load = input.required<FieldTree<Load>>();

  private readonly mceApiService = inject(MceApiService);

  framesRx = rxResource({
    params: () => ({}),
    stream: () =>
      this.mceApiService.v1ProtectionFrames('ACB_IEC60947')
  });
  frames = computed(() => this.framesRx.value() ?? []);

  ratingsRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of([])
        : this.mceApiService.v1ProtectionRatings('ACB_IEC60947', params.assemblyGuid)
  });
  ratings = computed(() => this.ratingsRx.value() ?? []);

  autoRatingRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value(), ib: this.load().ib().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of({ i: 0 })
        : this.mceApiService.v1ProtectionRatingsAuto('ACB_IEC60947', params.assemblyGuid, params.ib)
  });
  autoRating = computed<InDto>(() => this.autoRatingRx.value() ?? { i: 0 });

  setAutoRating = effect(() => {
    if (!this.form().autoRating().value()) return;
    const autoRating = this.autoRating().i;
    if (autoRating === undefined || autoRating === 0) return;

    if (autoRating !== this.form().rating().value())
      this.form().rating().value.set(autoRating);
  });

  /// Ensure the frames has a valid selection.
  frameToValid = effect(() => {
    const frameGuid = this.frames()[0]?.value;
    if (frameGuid !== undefined && this.form().assemblyGuid().value() === '')
      this.form().assemblyGuid().value.set(frameGuid);
  });

  setManualMissingRating = effect(() => {
    if (this.form().autoRating().value()) return;
    if (this.ratings().length === 0) return;

    if (this.ratings().find(x => x.i === this.form().rating().value()) === undefined) {
      const rating = this.ratings()[this.ratings().length - 1].i;
      console.log('rating:', this.ratings(), rating);
      if (rating !== undefined) {
        this.form().rating().value.set(rating);
      }
    }
  });


}
